import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

// components
import ActionButton from "../../../components/buttons/ActionButton/ActionButton.component";
import DeleteObjectWithModal from "../../../blockGroups/deletion/DeleteObjectWithModal/DeleteObjectWithModal.blockgroup";

// collections
import JobName from "./collections/JobName.collections";
import JobInfo from "./collections/JobInfo.collection";
import JobCustomer from "./collections/JobCustomer.collection";
import JobDetailEquipmentRequests from "./collections/JobDetailEquipmentRequests/JobDetailEquipementRequests.collection";
import JobDetailDays from "./collections/JobDetailDays/JobDetailDays.collection";
import JobDetailEquipmentLogs from "./collections/JobDetailEquipmentLogs/JobDetailEquipmentLogs.collection";

// forms
import EditJobController from "./forms/editJob/EditJob.form.controller";

// wrapper
import WithGqlQuery from "../../../wrappers/gql/WithGqlQuery/WithGqlQuery.wrapper";

// graphql
import {
  GET_ALL_JOB_DAYS_FOR_JOB,
  GET_ALL_EQUIPMENT_REQUESTS_FOR_JOB,
  GET_INVENTORY_LOGS_FOR_JOB,
} from "../../../graphql/job/job.queries";
import { DELETE_JOB } from "../../../graphql/job/job.mutations";

// mui
import { Grid, Container, Alert } from "@mui/material";

export default function JobDetail() {
  const { jobId: jobIdParam } = useParams();

  // Parse once, keep strictly a number (or null if invalid)
  const jobIdInt = useMemo(() => {
    const n = Number(jobIdParam);
    return Number.isInteger(n) ? n : null;
  }, [jobIdParam]);

  // modals
  const [showEditModal, setShowEditModal] = useState(false);

  // If we can’t coerce to a number, don’t fire queries with a bad id
  if (jobIdInt === null) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Alert severity="error" variant="filled">
          Invalid Job ID in URL.
        </Alert>
      </Container>
    );
  }

  return (
    <div id="JobDetail">
      <EditJobController
        visible={showEditModal}
        id={jobIdInt} // ← guaranteed number
        closeForm={() => setShowEditModal(false)}
      />

      {/* give Container a valid maxWidth to avoid prop-type warnings */}
      <Container maxWidth="lg" sx={{ mb: 4 }} disableGutters>
        <Grid container spacing={2} justifyContent="space-between">
          <Grid item xs={6}>
            <JobName id={jobIdInt} />
          </Grid>
          <Grid
            container
            item
            xs={6}
            spacing={2}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Grid item>
              <ActionButton
                label="Edit"
                onClick={() => setShowEditModal(true)}
              />
            </Grid>
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="lg" disableGutters>
        <Grid container spacing={2} justifyContent="space-between">
          <Grid item xs={6}>
            <JobInfo id={jobIdInt} />
          </Grid>
          <Grid item xs={6}>
            <JobCustomer id={jobIdInt} />
          </Grid>
        </Grid>
      </Container>

      {/* All queries receive a hard number */}
      <WithGqlQuery
        component={JobDetailDays}
        query={GET_ALL_JOB_DAYS_FOR_JOB}
        variables={{ id: jobIdInt }}
      />

      <WithGqlQuery
        component={JobDetailEquipmentRequests}
        query={GET_ALL_EQUIPMENT_REQUESTS_FOR_JOB}
        variables={{ id: jobIdInt }}
      />

      <WithGqlQuery
        component={JobDetailEquipmentLogs}
        query={GET_INVENTORY_LOGS_FOR_JOB}
        variables={{ id: jobIdInt }}
      />

      <DeleteObjectWithModal
        id={jobIdInt}
        mutation={DELETE_JOB}
        deleteLabel="Delete Job"
        deleteTitle="Delete Job"
        deleteMessage="Are you sure you want to delete this Job? This will delete all Job Days and Crew Member Assignments associated with this Job"
        successObjectKey="deleteJob"
        entityName="Job"
      />
    </div>
  );
}
